<template>
    <div class="card mt-3">
        <div class="card-header">
            <h5 class="mb-0">{{ $t("bozp.title") }}</h5>
        </div>
        <!-- rendered after load, otherwise the loaded version would overwrite text typed in the meantime -->
        <div v-if="loaded" class="card-body">
            <p class="text-muted">{{ $t("bozp.settings.description") }}</p>

            <div v-if="!latest" class="alert alert-secondary">{{ $t("bozp.settings.notSet") }}</div>
            <div v-else-if="!enabled" class="alert alert-secondary">
                {{ $t("bozp.settings.disabled", { version: latest.version }) }}
            </div>
            <div v-else class="alert alert-success d-flex justify-content-between align-items-center">
                <span>
                    {{ $t("bozp.settings.current", {
                        version: latest.version,
                        name: latest.created_by_name || "–",
                        date: $d(new Date(latest.created_at), 'short'),
                    }) }}
                </span>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="openPreview">
                    {{ $t("bozp.settings.preview") }}
                </button>
            </div>

            <label for="bozpText" class="form-label">{{ $t("bozp.settings.text") }}</label>
            <textarea id="bozpText" v-model="text" class="form-control mb-3" rows="8"></textarea>

            <label for="bozpFile" class="form-label">{{ $t("bozp.settings.pdf") }}</label>
            <div v-if="latest && latest.file_name" class="mb-2">
                <span v-if="!removeFile">
                    {{ $t("bozp.settings.currentPdf") }}: <b>{{ latest.file_name }}</b>
                    <button type="button" class="btn btn-sm btn-link text-danger" @click="removeFile = true">
                        {{ $t("bozp.settings.removePdf") }}
                    </button>
                </span>
                <span v-else class="text-danger">
                    {{ $t("bozp.settings.pdfWillBeRemoved") }}
                    <button type="button" class="btn btn-sm btn-link" @click="removeFile = false">
                        {{ $t("bozp.settings.undoRemove") }}
                    </button>
                </span>
            </div>
            <input id="bozpFile" ref="file" type="file" accept="application/pdf" class="form-control mb-2"
                @change="onFileChange">
            <p class="text-muted small">{{ $t("bozp.settings.emptyHint") }}</p>

            <button type="button" class="btn btn-primary" :disabled="!changed || saving" @click="save">
                {{ $t("bozp.settings.save") }}
            </button>
        </div>
    </div>

    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="previewModal">
        <div class="modal-dialog modal-fullscreen">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">
                        {{ $t("bozp.title") }}
                        <span v-if="latest"> – {{ $t("bozp.version", { version: latest.version }) }}</span>
                    </h3>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body overflow-hidden preview_body">
                    <BozpViewer v-if="latest && previewShown" :key="latest.id" :version="latest" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BozpViewer from '@/components/BozpViewer.vue';
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";

export default {
    components: {
        BozpViewer,
    },
    data() {
        return {
            loaded: false,
            latest: null,
            enabled: false,
            text: "",
            newFile: null,
            removeFile: false,
            saving: false,
            previewShown: false,
        };
    },
    computed: {
        changed() {
            return this.text.trim() !== (this.latest?.text || "") || !!this.newFile || this.removeFile;
        },
    },
    mounted() {
        this.$refs.previewModal.addEventListener("shown.bs.modal", () => { this.previewShown = true; });
        this.$refs.previewModal.addEventListener("hidden.bs.modal", () => { this.previewShown = false; });
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            this.$store.getters.api.get(`/dashboard/${this.$route.params.slug}/gatehouse/bozp`)
                .then(response => {
                    this.apply(response.data);
                })
                .catch(error => {
                    console.error("Error loading BOZP", error);
                });
        },
        apply(data) {
            this.loaded = true;
            this.latest = data.bozp;
            this.enabled = data.bozp_enabled;
            this.text = data.bozp?.text || "";
            this.newFile = null;
            this.removeFile = false;
            if (this.$refs.file) {
                this.$refs.file.value = "";
            }
        },
        onFileChange(event) {
            this.newFile = event.target.files[0] || null;
            if (this.newFile) {
                this.removeFile = false;
            }
        },
        save() {
            const formData = new FormData();
            formData.append("text", this.text);
            formData.append("remove_file", this.removeFile);
            if (this.newFile) {
                formData.append("file", this.newFile);
            }

            this.saving = true;
            this.$store.getters.api.post(`/dashboard/${this.$route.params.slug}/gatehouse/bozp/add`, formData)
                .then(response => {
                    this.apply(response.data);
                })
                .catch(error => {
                    console.error("Error saving BOZP", error);
                    const code = error.response?.data?.error_code;
                    alert(this.$t([562, 564, 565, 566].includes(code) ? `error.${code}` : "error.undefined"));
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        openPreview() {
            Modal.getOrCreateInstance(this.$refs.previewModal).show();
        },
    },
};
</script>

<style scoped>
/* lets the viewer, not the modal body, scroll */
.preview_body {
    min-height: 0;
}
</style>
